import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import './EventDetailsModal.css';
import supabase from '../../../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';

const EventDetailsModal = ({
  event,
  isAttending,
  onClose,
  onAttend,
  onLeave,
  onEdit,
  onCancel,
  onShare,
  isCreator,
  attendees = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedEvent, setEditedEvent] = useState(event);
  const [eventImage, setEventImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setEventImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setEventImage(null);
    setImagePreview(null);
  };

  const handleEdit = async () => {
    if (isEditing) {
      setUploading(true);
      try {
        let event_banner_url = editedEvent.event_banner_url;

        if (eventImage) {
          const fileExt = eventImage.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}.${fileExt}`;
          const filePath = `event-banners/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('banners')
            .upload(filePath, eventImage, { upsert: true });

          if (uploadError) {
            throw uploadError;
          }

          const {
            data: { publicUrl },
          } = supabase.storage.from('banners').getPublicUrl(filePath);

          event_banner_url = publicUrl;
        }

        await onEdit(event.id, { ...editedEvent, event_banner_url });
        setEventImage(null);
        setImagePreview(null);
        setIsEditing(false);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Error uploading image. Please try again.');
      } finally {
        setUploading(false);
      }
    } else {
      setIsEditing(true);
      setImagePreview(event.event_banner_url);
    }
  };

  const handleCancelEvent = () => {
    const isCancelled = event.status === 'cancelled';
    const message = isCancelled
      ? 'This event is already marked as cancelled. Delete it permanently? This will remove the event and its attendees.'
      : 'Mark this event as cancelled? Attendees will see it as cancelled until it is deleted.';

    if (window.confirm(message)) {
      onCancel(event.id);
    }
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="event-details-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          ×
        </button>

        {isEditing ? (
          <div className="edit-form">
            <div className="form-group">
              <label>Title</label>
              <input
                value={editedEvent.title}
                onChange={(e) =>
                  setEditedEvent({ ...editedEvent, title: e.target.value })
                }
                placeholder="Event Title"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                value={editedEvent.date}
                onChange={(e) =>
                  setEditedEvent({ ...editedEvent, date: e.target.value })
                }
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Time</label>
              <input
                type="time"
                value={editedEvent.time}
                onChange={(e) =>
                  setEditedEvent({ ...editedEvent, time: e.target.value })
                }
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Language</label>
              <select
                value={editedEvent.language}
                onChange={(e) =>
                  setEditedEvent({ ...editedEvent, language: e.target.value })
                }
                className="form-input"
              >
                <option value="">Select Language</option>
                <option value="English">English</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Chinese">Chinese</option>
                <option value="Japanese">Japanese</option>
              </select>
            </div>

            <div className="event-image-upload form-group">
              <label>
                Event Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <div
                  className={`image-upload-area ${
                    imagePreview ? 'has-image' : ''
                  }`}
                  onClick={() =>
                    !imagePreview &&
                    document.querySelector('input[type="file"]').click()
                  }
                >
                  {imagePreview ? (
                    <div className="image-preview-container">
                      <img src={imagePreview} alt="Event preview" />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <span>Click to upload event image</span>
                      <small>(Optional)</small>
                    </div>
                  )}
                </div>
              </label>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editedEvent.description}
                onChange={(e) =>
                  setEditedEvent({
                    ...editedEvent,
                    description: e.target.value,
                  })
                }
                placeholder="Event Description"
                className="form-textarea"
                rows="4"
              />
            </div>

            <div className="edit-actions">
              <button
                onClick={handleEdit}
                className="save-btn"
                disabled={uploading}
              >
                Save Changes
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="event-header">
              {event.event_banner_url && (
                <div className="event-image">
                  <img src={event.event_banner_url} alt={event.title} />
                  {isCreator && (
                    <button
                      type="button"
                      className="event-modal-banner-edit-btn"
                      title="Change banner image"
                      onClick={() => setIsEditing(true)}
                    >
                      <FaEdit />
                    </button>
                  )}
                </div>
              )}
              {event.status === 'cancelled' && (
                <span className="event-status-badge cancelled">Cancelled</span>
              )}
              <h2>{event.title}</h2>
              {isCreator && <span className="host-badge">You're the host</span>}
            </div>

            <div className="event-info">
              <p>
                <span>Date:</span>{' '}
                {new Date(event.date).toLocaleDateString()}
              </p>
              <p>
                <span>Time:</span> {event.time}
              </p>
              <p>
                <span>Language:</span> {event.language}
              </p>
              <p className="description">{event.description}</p>
              {event.status === 'cancelled' && (
                <div className="event-cancelled-message">
                  This event has been cancelled by the host.
                </div>
              )}
            </div>

            <div className="attendees-section">
              <h3>Attendees ({attendees.length})</h3>
              <div className="attendees-scroll">
                <div className="attendees-list">
                  {attendees.map((attendee) => (
                    <div key={attendee.id} className="attendee-item">
                      <img
                        src={attendee.avatar_url || '/default-avatar.png'}
                        alt={attendee.name}
                        className="attendee-avatar"
                      />
                      <div className="attendee-meta">
                        <span
                          className="attendee-name"
                          title={attendee.name}
                        >
                          {attendee.name}
                        </span>
                        {attendee.role && (
                          <span className={`role-badge ${attendee.role}`}>
                            {attendee.role === 'host'
                              ? 'Host'
                              : attendee.role}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              {isCreator ? (
                <div className="host-actions">
                  <button onClick={handleEdit} className="edit-btn">
                    Edit Event
                  </button>
                  <button
                    onClick={handleCancelEvent}
                    className="cancel-btn"
                  >
                    {event.status === 'cancelled' ? 'Delete Event' : 'Cancel Event'}
                  </button>
                </div>
              ) : event.status !== 'cancelled' ? (
                <button
                  onClick={() =>
                    isAttending ? onLeave(event.id) : onAttend(event.id)
                  }
                  className={`attend-btn ${
                    isAttending ? 'attending' : ''
                  }`}
                >
                  {isAttending ? 'Leave Event' : 'Join Event'}
                </button>
              ) : null}

              {event.status !== 'cancelled' && (isAttending || isCreator) && event.room_id && (
                <button
                  type="button"
                  className="join-virtual-btn"
                  onClick={() =>
                    navigate(`/app/virtualcafe/${event.room_id}`)
                  }
                >
                  Join Virtual Room
                </button>
              )}

              <button
                onClick={() => onShare(event.id)}
                className="share-btn"
              >
                Share Event
              </button>

              {/* Deep-link CTA to the full event page (About tab) in a new tab */}
              <button
                type="button"
                className="join-virtual-btn"
                onClick={() => {
                  onClose();
                  window.open(`/app/events/${event.id}`, '_blank', 'noopener,noreferrer');
                }}
              >
                Go to Event Page
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EventDetailsModal;